import _ from 'lodash';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'shortcss'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/shortcss/lib/index.js' implicitly has an 'any' type.
import shortcss from 'shortcss';
import {
  splitCssValue,
  cssToPropertyList,
  cssToPropertyMap,
} from '@packages/systems/core/utils/CssStringUtils';
import {hasValue} from '@packages/systems/core/utils/JsUtils';
import {
  CSS_PROPERTY_DETAILS,
  BGIMAGE_SUBVALUE_PROPS,
  BGIMAGE_ALL_PROPS,
  TRANSITION_SUBVALUE_PROPS,
} from './CssPropertyUtils';
import {sortCssProperties} from '@packages/systems/style-legacy/css-properties';
import * as CssUtils from '@packages/systems/style-legacy/utils';
import * as ShadowParser from '../parsers/ShadowParser';
import {formatShadowParts} from './StyleStoreFormatter';
import {parseShadowParts} from './StyleStoreParser';

import {hasSwatchPlaceholder} from '@packages/systems/style-legacy/swatches';
import {compileLess as sharedCompileLess} from '@packages/systems/style-legacy/style-less';

import type {
  AssetState,
  StyleSheetImageResolverInterface,
} from '@packages/systems/designer/assets';
import type {
  RawStyleBlockServerDataType,
  StyleBlockVariantType,
  CssVariablesStateType,
  MixinKey,
  MixinMap,
  AppliedMixinKey,
} from '@packages/systems/core/utils/StyleTypes';
import type {Font} from '@packages/systems/core/constants/Fonts';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {containsCssCustomValue} from '@packages/systems/style/raw-values';
import {Plugins} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {emptyArray} from '@packages/utilities/fp/utils';
import {defer} from '@packages/utilities/lazy';

const INVALID_PERSPECTIVE_VALUE_RE =
  /^(-[0-9]*\.?[0-9]*|0|0?\.[0-9]*)([a-z]+)?/;
const INVALID_PERSPECTIVE_TRANSFORM_VALUE_RE =
  /perspective\((-[0-9]*\.?[0-9]*|0|0?\.[0-9]*)([a-z]+)?\)/;

/**
 * Converts an array of objects into an object with keys of arrays.
 * @param  {Array}  arr An array in the shape of [ { a: 1, b: 2 }, { a: 5, b: 10 } ]
 * @return {Object}     An combined object where keys map to arrays of values, e.g. { a: [1, 5], b: [2, 10] }
 */
function transpose(
  arr: Array<{
    [key: string]: number;
  }>
): {
  [key: string]: Array<number>;
} {
  return arr.reduce<Record<string, any>>((result, value) => {
    for (const key in value) {
      result[key] = result[key] || [];
      result[key].push(value[key]);
    }

    return result;
  }, {});
}

// CSS properties in styleText that will be moved to styleLess if they're missing
const TEXT_PROPERTIES_TO_PATCH = {
  'backface-visibility': true,
  'background-color': true,
  'border-top-width': true,
  'border-top-style': true,
  'border-top-color': true,
  'border-right-width': true,
  'border-right-style': true,
  'border-right-color': true,
  'border-bottom-width': true,
  'border-bottom-style': true,
  'border-bottom-color': true,
  'border-left-width': true,
  'border-left-style': true,
  'border-left-color': true,
  'border-bottom-left-radius': true,
  'border-bottom-right-radius': true,
  'border-top-left-radius': true,
  'border-top-right-radius': true,
  bottom: true,
  'box-shadow': true,
  'box-sizing': true,
  clear: true,
  color: true,
  'column-count': true,
  'column-gap': true,
  'column-rule-color': true,
  'column-rule-style': true,
  'column-rule-width': true,
  'column-span': true,
  cursor: true,
  direction: true,
  display: true,
  filter: true,
  float: true,
  'font-size': true,
  'font-smoothing': true,
  'font-style': true,
  'font-weight': true,
  height: true,
  left: true,
  'letter-spacing': true,
  'line-height': true,
  'list-style-type': true,
  'margin-bottom': true,
  'margin-left': true,
  'margin-right': true,
  'margin-top': true,
  'max-height': true,
  'max-width': true,
  'min-height': true,
  'min-width': true,
  opacity: true,
  'outline-color': true,
  'outline-offset': true,
  'outline-style': true,
  'outline-width': true,
  overflow: true,
  'padding-bottom': true,
  'padding-left': true,
  'padding-right': true,
  'padding-top': true,
  perspective: true,
  'perspective-origin': true,
  position: true,
  right: true,
  'text-align': true,
  'text-decoration': true,
  'text-indent': true,
  'text-shadow': true,
  'text-transform': true,
  top: true,
  transform: true,
  'transform-origin': true,
  'transition-property': true,
  'transition-duration': true,
  'transition-timing-function': true,
  'vertical-align': true,
  width: true,
  'z-index': true,
} as const;

// styleLess properties that can be replaced by styleText values if they exist
const SAFE_LESS_PROPS_TO_OVERRIDE = {
  'background-color': true,
  bottom: true,
  clear: true,
  color: true,
  display: true,
  float: true,
  'font-size': true,
  'font-style': true,
  'font-weight': true,
  height: true,
  left: true,
  'letter-spacing': true,
  'line-height': true,
  'max-height': true,
  'max-width': true,
  'min-height': true,
  'min-width': true,
  opacity: true,
  'outline-color': true,
  'outline-offset': true,
  'outline-style': true,
  'outline-width': true,
  perspective: true,
  'perspective-origin': true,
  position: true,
  right: true,
  'text-align': true,
  'text-decoration': true,
  'text-indent': true,
  'text-transform': true,
  top: true,
  'vertical-align': true,
  width: true,
  'z-index': true,
} as const;

// styleLess properties that can be safely dropped if styleText doesn't have a value defined
const SAFE_LESS_PROPS_TO_DROP_IF_NO_PUBLISHED_VALUE = {
  'background-attachment': true,
  'background-image': true,
  'background-position': true,
  'background-repeat': true,
  'background-size': true,
  'border-bottom-color': true,
  'border-bottom-left-radius': true,
  'border-bottom-right-radius': true,
  'border-bottom-style': true,
  'border-bottom-width': true,
  'border-left-color': true,
  'border-left-style': true,
  'border-left-width': true,
  'border-right-color': true,
  'border-right-style': true,
  'border-right-width': true,
  'border-top-color': true,
  'border-top-left-radius': true,
  'border-top-right-radius': true,
  'border-top-style': true,
  'border-top-width': true,
  'box-shadow': true,
  filter: true,
  'margin-bottom': true,
  'margin-left': true,
  'margin-right': true,
  'margin-top': true,
  'padding-bottom': true,
  'padding-left': true,
  'padding-right': true,
  'padding-top': true,
  perspective: true,
  'perspective-origin': true,
  'text-shadow': true,
  transform: true,
  'transition-duration': true,
  'transition-property': true,
  'transition-timing-function': true,
  'vertical-align': true,
} as const;

/**
 * Converts a server-side main rule or variant rule object to the styleLess format the client expects.
 * This method contains a ton of extra logic to deal with mismatched styleText and styleLess prior to styleText being removed in styles-next.
 * @param  {Object}     variantData         The server-side block or variant data, e.g. { type: 'class', name: 'Button', styleLess: '...' }
 * @param  {AssetState} options.assetState  The Flux AssetStore state for the site being imported.
 * @param  {Object[]}   options.errors      An array of errors encountered during a full site import pass.
 *                                          This is TEMPORARY and only needed to preserve legacy styleText values for debugging/backup purposes.
 * @return {String}                         The styleLess text, with properties sorted logically.
 */
export function importLess(
  variantData: RawStyleBlockServerDataType | StyleBlockVariantType,
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type '{ assetState: AssetState; errors: { [key: string]: { sel?: string | undefined; text?: string | undefined; less?: string | undefined; }; }[]; }': assetState, errors
  {
    assetState,
    errors = [],
  }: {
    assetState: AssetState;
    errors: {
      [key: string]: {
        sel?: string;
        text?: string;
        less?: string;
      };
    }[];
  } = {}
) {
  let styleLessRemote = variantData.styleLess;
  const styleTextRemote = variantData.styleText;
  const styleTextRemoteExists = hasValue(styleTextRemote);

  // Some legacy sites don't have styleLess, and some older bugs have caused styleLess to be null
  // So just assume that it's empty, and defer to the existing import logic to carry values across from styleText
  if (!hasValue(styleLessRemote)) {
    styleLessRemote = '';
  }

  // Make sure that CSS properties are in expected order
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const lessProps = cssToPropertyList(styleLessRemote);
  sortCssProperties(
    lessProps as Array<{property: string; value: string}>,
    'property',
    (propVal) =>
      typeof propVal.value === 'string' && containsCssCustomValue(propVal.value)
  );

  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const styleTextMap = cssToPropertyMap(styleTextRemote);
  const styleLessMap = _.fromPairs(
    // @ts-expect-error - TS7031 - Binding element 'property' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type.
    lessProps.map(({property, value}) => {
      return [property, value];
    })
  );

  // --- Helper functions --- //
  const _findLessProp = (name: string) =>
    _.find(lessProps, ({property}) => property === name);

  const _removeLessProps = (propsToRemove: Array<string>) => {
    propsToRemove.forEach((propName) => {
      const prop = _.find(lessProps, ({property}) => property === propName);
      if (prop) {
        lessProps.splice(_.indexOf(lessProps, prop), 1);
      }
    });
  };

  const _upsertLessProp = (property: string, value: string | null): boolean => {
    const lessValue = _findLessProp(property);
    if (lessValue) {
      if (lessValue.value === value) {
        return false;
      }
      lessValue.value = value;
    } else if (!lessValue) {
      lessProps.push({property, value});
    }

    return true;
  };

  const _upsertMultipleLessProps = (propMap: Map<string, unknown>): boolean => {
    let anyValueChanged = false;

    _.toPairs(propMap).forEach(([key, value]: [any, any]) => {
      anyValueChanged = _upsertLessProp(key, value) || anyValueChanged;
      styleTextMap[key] = value; // Attach for easy access later to help decide which out-of-sync styleLess props to drop
    });

    return anyValueChanged;
  };

  const _existingLessProps = (propNames: Array<string>) => {
    return _.compact(_.map(propNames, (prop) => _findLessProp(prop)));
  };

  const _missingLessPropsNames = (propNames: Array<string>) => {
    return _.filter(propNames, (prop) => !_findLessProp(prop));
  };

  const _logError = (tag: string, customProps: any) => {
    errors.push({
      [tag as string]: {
        sel: variantData.sel,
        text: variantData.styleText,
        less: variantData.styleLess,
        ...customProps,
      },
    });
  };

  const _convertBgImagePartToLess = (cssValue: string): string => {
    if (/^url\(["']{0,1}([^'"\)]*)["']{0,1}\)$/.test(cssValue)) {
      const imageUrl = CssUtils.unwrapValue(cssValue, true).inside;
      const imageRecord =
        assetState && imageUrl ? assetState.getByUrl(imageUrl) : null;
      if (imageRecord) {
        return '@img_' + imageRecord._id;
      }

      return '@img_example_bg';
    }

    return cssValue; // Gradients
  };

  // Detect improperly commented BG image parts and try to recover
  // Must do this before styleText values fill in missing styleLess background-* props
  _fixCorruptBackgroundParts(
    styleTextMap,
    _findLessProp,
    _upsertLessProp,
    _convertBgImagePartToLess,
    _logError
  );

  const styleTextMapKeys = Object.keys(styleTextMap);
  const originalStyleTextKeys = styleTextMapKeys;
  originalStyleTextKeys.forEach((prop) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<WebflowCssProperty, CssPropertyDetail>>'.
    const propDetails = CSS_PROPERTY_DETAILS[prop] || {};
    const propTextValue = styleTextMap[prop];
    const propLessValue = styleLessMap[prop];

    if (prop in TEXT_PROPERTIES_TO_PATCH && propLessValue === undefined) {
      lessProps.push({property: prop, value: propTextValue});
      _logError('MissingCssProp', {[prop as string]: propTextValue});
    } else if (
      propDetails.dimensional &&
      propTextValue &&
      propLessValue &&
      propTextValue !== propLessValue &&
      // @ts-expect-error - TS2339 - Property 'trim' does not exist on type '{}'.
      /^(auto|none|initial|NaN).+$/.test(propLessValue.trim())
    ) {
      // When styleText has "width: 10px;" but styleLess has an illegal "width: autopx;" or "max-width: nonepx;", grab value from styleText
      _upsertLessProp(prop, propTextValue);
      _logError('IllegalUnit', {prop, from: propLessValue, to: propTextValue});
    } else if (
      prop === 'column-rule' &&
      _missingLessPropsNames([
        'column-rule-style',
        'column-rule-width',
        'column-rule-color',
      ]).length === 3
    ) {
      // Fix missing column-rule values in styleLess when shorthand is present in styleLess, assumes column props are collapsed with CssShorthandUtils.collapseColumnRule()
      const longhandProps = shortcss.expand(prop, propTextValue);
      if (Object.keys(longhandProps).length === 3) {
        _.toPairs(longhandProps).forEach(([key, value]: [any, any]) => {
          _upsertLessProp(key, value);
        });
        _logError('MissingColProps', {parsed: propTextValue});
      }
    } else if (
      prop === 'transform' &&
      propLessValue &&
      propTextValue &&
      propTextValue !== propLessValue
    ) {
      // Log (and patch when safe) mismatching styleText / styleLess transform parts
      const transformTextParts = splitCssValue(propTextValue, ' ');
      // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'string'.
      const transformLessParts = splitCssValue(propLessValue, ' ');
      const transformLessPartsWithoutComments = _.filter(
        transformLessParts,
        (part) => part.indexOf('/*') === -1
      );

      // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'string'.
      if (propTextValue !== 'none' && _.startsWith(propLessValue, 'none')) {
        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('TransformNoneReplace');
        _upsertLessProp(prop, propTextValue);
      } else if (
        !_.isEqual(transformLessPartsWithoutComments, transformTextParts)
      ) {
        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('TransformMismatch');
        if (transformLessParts.length < transformTextParts.length) {
          // Fill in missing parts only if existing ones match exactly
          const leadTextParts = transformTextParts.slice(
            0,
            transformLessParts.length
          );
          if (_.isEqual(leadTextParts, transformLessParts)) {
            const missingParts = transformTextParts.slice(
              transformLessParts.length
            );
            const newLessValue = [...transformLessParts, ...missingParts].join(
              ' '
            );
            _upsertLessProp(prop, newLessValue);
          }
        }
      }
    } else if (prop === 'transition' && propTextValue) {
      const existingTransitionSubvalues = _existingLessProps(
        // @ts-expect-error - TS2345 - Argument of type 'readonly ["transition-property", "transition-duration", "transition-timing-function"]' is not assignable to parameter of type 'string[]'.
        TRANSITION_SUBVALUE_PROPS
      );
      const styleTextParts = splitCssValue(propTextValue, ',');

      // Explode shorthand into individual transition-* props
      const subvalueParts = _.fromPairs(
        TRANSITION_SUBVALUE_PROPS.map((p) => [p, []])
      );
      styleTextParts.forEach((part) => {
        const longhandValues = shortcss.expand('transition', part);

        TRANSITION_SUBVALUE_PROPS.forEach((subprop) => {
          // @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'.
          subvalueParts[subprop].push(longhandValues[subprop]);
        });
      });

      // Write out all expanded transition-* props into the styleText map for easier lookup
      TRANSITION_SUBVALUE_PROPS.forEach((subprop) => {
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        styleTextMap[subprop] = subvalueParts[subprop].join(', ');
      });

      // If there are already some subvalues, determine the count of parts inside of each subvalue
      // We'll only continue this migration if the # of parts is the exact same as in the shorthand
      let commonExistingSubvalueCount = styleTextParts.length;
      if (existingTransitionSubvalues.length) {
        for (let i = 0; i < existingTransitionSubvalues.length; i++) {
          const subvalueChunks = splitCssValue(
            existingTransitionSubvalues[i].value,
            ','
          );
          if (subvalueChunks.length !== commonExistingSubvalueCount) {
            // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'number'.
            commonExistingSubvalueCount = undefined;
          }
        }
      }
      // Walk through each subproperty, and replace the styleLess value if it is missing or doesn't match
      let changed = false;
      TRANSITION_SUBVALUE_PROPS.forEach((subprop) => {
        const styleTextValue = styleTextMap[subprop];
        const styleLessValue = styleLessMap[subprop];
        if (
          styleTextValue &&
          // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'string'.
          !(styleLessValue && _hasComment(styleLessValue)) &&
          styleTextValue !== styleLessValue
        ) {
          changed = _upsertLessProp(subprop, styleTextValue) || changed;
        }
      });

      if (changed) {
        _logError('FixedTransitionProps', {parsed: propTextValue});
      }
    } else if (
      (prop === 'width' ||
        prop === 'min-width' ||
        prop === 'max-width' ||
        prop === 'height' ||
        prop === 'min-height' ||
        prop === 'max-height') &&
      propTextValue &&
      propLessValue === 'auto' &&
      propTextValue !== 'auto'
    ) {
      // Fix corrupt styleLess values that equal "auto" but have published non-auto styleText values
      _upsertLessProp(prop, propTextValue);
      _logError('OverrideAutoDim', {prop, to: propTextValue});
    } else if (
      prop === 'background-image' &&
      propTextValue === 'none' &&
      // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string | undefined'.
      !_.startsWith(propLessValue, 'none')
    ) {
      // Reset mismatching background-image props when styleText has background-image: none; and clear all background related sub parts in styleLess
      _upsertLessProp('background-image', 'none');
      // @ts-expect-error - TS2345 - Argument of type 'readonly ["background-position", "background-size", "background-repeat", "background-attachment"]' is not assignable to parameter of type 'string[]'.
      _removeLessProps(BGIMAGE_SUBVALUE_PROPS);
      // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
      _logError('ResetBgImage');
    } else if (
      /^background-(position|size|repeat|attachment)$/.test(prop) &&
      propTextValue &&
      propLessValue === undefined &&
      styleTextMap['background-image'] !== 'none'
    ) {
      // Patch background-* properties manually - we do this separately because we want it to run only in the non background-image: none case
      lessProps.push({property: prop, value: propTextValue});
      _logError('MissingBgImageProp', {[prop as string]: propTextValue});
    } else if (prop === 'background-image' && propTextValue) {
      const propTextParts = splitCssValue(propTextValue, ',');
      const propLessParts = propLessValue
        ? // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'string'.
          splitCssValue(propLessValue, ',')
        : [];
      const cleanedPropLessParts = propTextParts.map(_convertBgImagePartToLess);
      const cleanedLessValue = cleanedPropLessParts.join(', ');

      // When background-image is missing in styleLess, try to recover by looking up images in the AssetStore
      // If an image is not found in the asset store, default to the example BG image
      if (propLessValue === undefined && cleanedPropLessParts.length) {
        _upsertLessProp(prop, cleanedLessValue);
        _logError('MissingBgImage', {lessParts: cleanedPropLessParts});
      } else if (
        propLessParts.length &&
        _.uniq(_.compact(propLessParts))[0] === '@img_example_bg'
      ) {
        // Otherwise if all the image parts are just example images, write in the translated styleText value
        if (_upsertLessProp(prop, cleanedLessValue)) {
          _logError('ReplacedExampleBg', {lessParts: cleanedPropLessParts});
        }
      }
    } else if (
      prop === 'font-family' &&
      propTextValue &&
      propLessValue === undefined
    ) {
      if (containsCssCustomValue(propTextValue)) {
        // Custom raw values are an explicit opt-out of Webflow's font-family
        // normalization — persist whatever the user typed verbatim, including
        // any fallback list (STYL-2402).
        lessProps.push({property: prop, value: propTextValue});
      } else {
        // Webflow only supports a single font family, so strip the fallback list.
        const familyParts = propTextValue.split(',');
        if (familyParts.length > 0 && familyParts[0]) {
          lessProps.push({
            property: prop,
            value: _.trim(familyParts[0], ' \'"'),
          });
        }
      }
    } else if (prop === 'margin' || prop === 'padding') {
      const sideValues = shortcss.expand(prop, propTextValue);
      if (_upsertMultipleLessProps(sideValues)) {
        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('MarginPaddingDims');
      }
    } else if (
      /^(margin|padding)-(top|right|bottom|left)$/.test(prop) &&
      propLessValue !== propTextValue
    ) {
      // Migrate mismatching margin-* and padding-* side values from styleText
      _upsertLessProp(prop, propTextValue);
    } else if (
      /^border(-(top|right|bottom|left|width|style|color))?$/.test(prop)
    ) {
      // Parse out individual border values and move them to styleLess
      const longhandMap = shortcss.expand(prop, propTextValue);

      const sides = ['top', 'right', 'bottom', 'left'];

      // If border has a styleLess value that contains a @swatch_12345678 placeholder for
      // each side, use that instead of styleText
      sides.forEach((side) => {
        const property = `border-${side}-color`;

        if (
          longhandMap[property] &&
          // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string | null'.
          hasSwatchPlaceholder(styleLessMap[property])
        ) {
          longhandMap[property] = styleLessMap[property];
        }
      });

      if (_upsertMultipleLessProps(longhandMap)) {
        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('MissingBorderParts');
      }
    } else if (prop === 'border-radius') {
      // Parse out individual border radius values and override anything conflicting in styleLess
      const longhandMap = shortcss.expand(prop, propTextValue);
      if (_upsertMultipleLessProps(longhandMap)) {
        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('MissingBorderRadiusParts');
      }
    } else if (/^border(-(top|right|bottom|left)){2}-radius$/.test(prop)) {
      // Prefer styleText values of individual border radius corners
      if (_upsertLessProp(prop, propTextValue)) {
        _logError('BorderRadiusOverride', {prop});
      }
    } else if (
      prop === 'overflow-x' &&
      propTextValue &&
      styleTextMap['overflow-y'] === propTextValue &&
      styleLessMap.overflow !== propTextValue
    ) {
      // Join individual overflow-x and overflow-y values into one single shorthand value
      _upsertLessProp('overflow', propTextValue);
    }
  });

  // Process each property and perform prop-specific cleanup
  const propsToDrop: Array<string> = [];
  // @ts-expect-error - TS7031 - Binding element 'property' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type.
  lessProps.forEach(({property, value}) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Partial<Record<WebflowCssProperty, CssPropertyDetail>>'.
    const propDetails = CSS_PROPERTY_DETAILS[property] || {};
    const styleTextValue = styleTextMap[property];
    const oldValue = value;

    // A fix for legacy sites (anything created before the 'styles-next' branch was merged) to correct
    // the improper "sans-serif" being included in the default "styleLess: Arial, sans-serif" <body> tag selector
    if (
      property === 'font-family' &&
      // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'StyleBlockVariantType | RawStyleBlockServerDataType'.
      variantData.type === 'tag' &&
      // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'StyleBlockVariantType | RawStyleBlockServerDataType'.
      variantData.name === 'body' &&
      // @ts-expect-error - TS2339 - Property 'comb' does not exist on type 'StyleBlockVariantType | RawStyleBlockServerDataType'.
      variantData.comb === null &&
      value === 'Arial, sans-serif'
    ) {
      value = 'Arial';
    }

    // Some sites have illegal dimensional properties "font-size: auto;" values that end up publishing, so defer to styleText or nuke them
    if (
      (property === 'font-size' || property === 'max-width') &&
      value === 'auto'
    ) {
      value = styleTextValue || null;
    }

    // Fix illegal hash in front of corrupt dimensional values, e.g. "border-top-width: #1px;" -> "border-top-width: 1px;"
    if (propDetails.dimensional && value && _.startsWith(value, '#')) {
      value = value.substring(1);
    }

    // Detect and drop unitless transition duration (which is not allowed in CSS)
    if (property === 'transition-duration') {
      const durationParts = splitCssValue(value, ',');
      const validDurationParts = _.compact(
        durationParts.map((part) => {
          if (part === '0') {
            return '0ms'; // Special case for 0
          } else if (/^\d+$/.test(part)) {
            return undefined;
          }

          return part;
        })
      );

      if (validDurationParts.length === durationParts.length) {
        const firstPart = validDurationParts[0];
        const emptyValue =
          hasValue(firstPart) &&
          _hasComment(firstPart) &&
          !_.startsWith(firstPart, '/*')
            ? firstPart.substr(0, firstPart.indexOf(' '))
            : '';
        value = CssUtils.joinParts(
          validDurationParts.map((part) => {
            const hidden = _hasComment(part);
            const css = hidden
              ? part.replace(/^[^\/]*\/\*,? ([^*,]+),? \*\/$/, '$1')
              : part;
            return {hidden, css};
          }),
          {emptyValue: emptyValue === '0' ? '0ms' : emptyValue}
        );
      } else {
        value = null;
      }
    }

    if (property === 'transform') {
      // If styleLess has a transform value, but styleText does not, drop it entirely
      if (
        oldValue &&
        !_.startsWith(oldValue, 'none') &&
        styleTextRemoteExists &&
        styleTextValue === undefined
      ) {
        value = null;
      } else {
        // To minimize impact of regex replacements, walk through each transform chunk separately
        value = _.compact(
          splitCssValue(value, ' ').map((chunk) => {
            // Fix invalid translate3d values that somehow made it into production with illegal %-unit values as the Z-axis transform
            if (
              chunk.indexOf('translate3d(') > -1 &&
              /translate3d\([-]?\d+[^,]*,\s?[-]?\d+[^,]*,\s?[-]?\d+%\)/.test(
                value
              )
            ) {
              chunk = chunk.replace(
                /translate3d\(([-]?\d+[^,]*),\s?([-]?\d+[^,]*),\s?[-]?\d+%\)/,
                'translate3d($1, $2, 0px)'
              );
            }

            // If there are any translation properties without units, force to pixels so we have parity between styleText/styleLess
            if (
              (chunk.indexOf('translate(') > -1 ||
                chunk.indexOf('translate3d(') > -1) &&
              /[( ]0[,)]/.test(value)
            ) {
              chunk = chunk.replace(/([( ])0([,)])/g, '$10px$2');
            }

            // If there's a corrupt scale3d value, ignore it completely (since it would not have been published anyway)
            if (/^(scale3d\([0-9.]*, [0-9.]*), null\)$/.test(chunk)) {
              chunk = null;
            }

            // Check for invalid perspective transform values.
            // perspective(n) with n < 1 is invalid since Chrome 93+.
            if (
              value &&
              chunk &&
              INVALID_PERSPECTIVE_TRANSFORM_VALUE_RE.test(chunk)
            ) {
              if (!_hasComment(chunk)) {
                chunk = '/* ' + chunk + ' */';
              }
            }

            return chunk;
          })
        ).join(' ');

        if (value !== oldValue) {
          _logError('TransformUnits', {from: oldValue, to: value});
        }
      }
    }

    // Fix legacy transition props getting prefixed transform values
    if (property === 'transition-property' && value) {
      value = value.replace(/\-webkit\-transform/g, 'transform');
    }

    if (property === 'background-image' && hasValue(value)) {
      const trimmedValue = _.trim(oldValue, ' ,');
      const styleTextParts = styleTextValue
        ? splitCssValue(styleTextValue, ',')
        : [];
      const styleLessParts = splitCssValue(trimmedValue, ',');
      const styleLessVisibleParts = styleLessParts.filter((part) => {
        return !_hasComment(part) && part !== 'none';
      });
      const allGradients =
        styleTextParts.length &&
        _.every(styleTextParts, (part) => {
          return (
            part.indexOf('gradient(') > 0 &&
            !_.startsWith(part, 'url(') &&
            part !== 'none'
          );
        });

      // Make sure the background-image value doesn't end with a trailing comma
      if (oldValue !== trimmedValue) {
        value = trimmedValue;
        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('TrailingBgImageComma');
      }

      if (
        allGradients &&
        styleTextParts.length !== styleLessVisibleParts.length
      ) {
        value = styleTextValue;
        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('CopyGradients');
      } else if (styleLessParts.length === 0 && styleTextParts.length > 0) {
        // When the styleLess background parts are totally missing (but background-image prop is present), try to recover from styleText
        const desiredStyleTextParts = styleTextParts.filter(
          (part) => part !== 'none'
        );
        if (desiredStyleTextParts.length) {
          const newPropLessParts = desiredStyleTextParts.map(
            _convertBgImagePartToLess
          );
          if (newPropLessParts.length) {
            value = newPropLessParts.join(', ');
            _logError('MissingBgImage', {newValue: value});
          }
        }
      } else if (
        styleTextParts.length > 0 &&
        styleTextParts.length !== styleLessParts.length &&
        !_hasComment(value)
      ) {
        // Migrate corrupted background-image values that contain multiple non-commented parts while other background-*
        // properties have subvalues commented, and styleText contains a different number of parts
        // Computed flags for whether subvalue chunks are hidden/commented or not
        // e.g. for "background-size: /* auto, */ cover; background-repeat: /* no-repeat, */ no-repeat;" will be:
        // [[ true, false ],
        //  [ true, false ]]
        const subvalueHiddenMatrix = _.compact(
          BGIMAGE_SUBVALUE_PROPS.map((subprop) => {
            const subpropValue = styleLessMap[subprop];
            if (!subpropValue) {
              return null;
            }

            // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'string'.
            return splitCssValue(subpropValue, ',').map(_hasComment);
          })
        );

        // The above matrix collapsed into one flat array of flags only if all flags in that column match exactly
        // For the above case, this will be [ true, false ], however if some subvalue chunks are hidden and some
        // are not in the same index (e.g. background-size is hidden in part 1 but background-repeat is not), it will
        // generate [ true, undefined ] and filter out to [ true ]
        const hiddenPartsTemplate = _.filter(
          styleLessParts.map((strPart, index) => {
            const hiddenFlagsMatch =
              _.uniq(subvalueHiddenMatrix.map((row) => row[index])).length ===
              1;
            return hiddenFlagsMatch
              ? // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                subvalueHiddenMatrix[0][index]
              : undefined;
          }),
          (entry) => entry !== undefined
        );

        // Only continue if all of the subvalues have the exact same commented chunk signature
        if (hiddenPartsTemplate.length === styleLessParts.length) {
          const partChunks = styleLessParts.map((cssPart, index) => {
            return {css: cssPart, hidden: hiddenPartsTemplate[index]};
          });
          const numberOfVisibleParts = partChunks.filter(
            (chunk) => !chunk.hidden
          ).length;

          // Only modify the LESS value if the new number of visible parts exactly matches what we have in CSS
          if (numberOfVisibleParts === styleTextParts.length) {
            const cleanBgImage = CssUtils.joinParts(partChunks);
            if (cleanBgImage !== oldValue) {
              value = cleanBgImage;
              _logError('CorruptBgImageComment', {
                from: oldValue,
                to: cleanBgImage,
              });
            }
          }
        } else if (
          styleLessVisibleParts.length > styleTextParts.length &&
          styleTextParts.length &&
          !_hasComment(value)
        ) {
          // Otherwise if there are more styleLess parts than what's visible in styleText, defer to styleText
          // But only do this if all the images are found in the image store
          const newPropLessParts = styleTextParts.map(
            _convertBgImagePartToLess
          );
          if (
            newPropLessParts.length &&
            _.every(newPropLessParts, (part) => {
              return part !== '@img_example_bg';
            })
          ) {
            value = newPropLessParts.join(', ');
            _logError('ReplacedBgImageParts', {newValue: value});
          }
        }
      } else if (
        styleTextParts.length === styleLessParts.length &&
        !_hasComment(value) &&
        assetState
      ) {
        // See if we have any orphaned/mismatching styleLess @img_123 references that don't point to any valid files
        const fixedFiles: Array<string> = [];
        const referenceStyleTextIds = styleTextParts.map(
          _convertBgImagePartToLess
        );

        const cleanedStyleLessParts = styleLessParts.map((part, index) => {
          if (_.startsWith(part, '@img_')) {
            const imageId = part.substr(5);
            const styleTextPart = referenceStyleTextIds[index];
            if (
              !assetState.getById(imageId) ||
              (styleTextPart &&
                _.startsWith(styleTextPart, '@img_') &&
                styleTextPart !== part)
            ) {
              fixedFiles.push(part);
              return styleTextPart;
            }
          }

          return part;
        });

        if (!_.isEqual(styleLessParts, cleanedStyleLessParts)) {
          value = cleanedStyleLessParts.join(', ');
          _logError('FixBgImageRefs', {fixedFiles});
        }
      } else if (
        styleTextParts.length === 1 &&
        allGradients &&
        styleLessParts.length === 2 &&
        styleTextParts[0].indexOf('gradient') >= 0 &&
        styleLessParts[0].indexOf('gradient') >= 0 &&
        _hasComment(styleLessParts[0]) &&
        styleLessParts[1].indexOf('@img_') >= 0 &&
        !_hasComment(styleLessParts[1])
      ) {
        // Detect commented-out gradients when they're published and comment out the image that should be commented instead
        value = styleLessParts
          .map((part, index) => {
            if (index === 0) {
              return _.trim(part, '/*, ');
            }
            if (index === 1) {
              return '/*, ' + part + ' */';
            }
            return part;
          })
          .join(' ');

        // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
        _logError('UncommentGradient');
      }
    }

    if (property === 'background-size') {
      const styleTextParts = splitCssValue(styleTextValue, ',');
      const backgroundSizeParts = splitCssValue(value, ',');
      const indexOfFirstVisiblePart = _.findIndex(
        backgroundSizeParts,
        (part) => !_hasComment(part)
      );

      // Grab the first background-size prop in styleText if it doesn't match the first non-commented styleLess part subvalue
      if (
        value.indexOf(' ') > 0 &&
        styleTextParts.length === 1 &&
        backgroundSizeParts.length >= 1 &&
        backgroundSizeParts[indexOfFirstVisiblePart] !== styleTextParts[0]
      ) {
        const fixedBgSizeValue = backgroundSizeParts
          .map((part, index) => {
            return index === indexOfFirstVisiblePart ? styleTextParts[0] : part;
          })
          .join(' ');

        if (fixedBgSizeValue !== value) {
          value = fixedBgSizeValue;
          _logError('CorruptBgSize', {replace: fixedBgSizeValue});
        }
      }

      // If the value inside a background-size contains any corrupt "123px cover" parts, remove the entire prop because it's invalid CSS
      if (/\b(cover|contain)\b/.test(value)) {
        const corruptKeywordParts = backgroundSizeParts.filter((chunk) => {
          return (
            !_hasComment(chunk) &&
            ((chunk !== 'cover' && /\bcover\b/.test(chunk)) ||
              (chunk !== 'contain' && /\bcontain\b/.test(chunk)))
          );
        });

        if (corruptKeywordParts.length) {
          _logError('CorruptBgSize', {parts: corruptKeywordParts});
          value = styleTextValue || null; // Grab from styleText value if it exists, otherwise drop the corrupt prop entirely
        }
      }

      // If we see invalid autopx values inside any part, grab the value from styleText
      if (/auto/.test(value)) {
        const corruptAutoParts = backgroundSizeParts.filter((chunk) => {
          return !_hasComment(chunk) && /^auto[a-z0-9]+/.test(chunk);
        });

        if (corruptAutoParts.length) {
          value = styleTextValue || null;
          _logError('CorruptBgSize', {parts: corruptAutoParts, replace: value});
        } else if (
          backgroundSizeParts.length === 1 &&
          _.startsWith(backgroundSizeParts[0], 'auto /*') &&
          styleTextValue &&
          styleTextValue !== 'auto'
        ) {
          value = styleTextValue;
          // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
          _logError('MismatchedBgSize');
        }
      }
    }

    if (property === 'background-position' && value) {
      const corruptPositionParts = splitCssValue(value, ',').filter((chunk) => {
        if (_hasComment(chunk)) {
          return false;
        }
        return splitCssValue(chunk, ' ').find((segment) => {
          return (
            !/^(auto|left|center|right|top|bottom)$/.test(segment.trim()) &&
            !CssUtils.isUnitValue(segment.trim())
          );
        });
      });

      if (corruptPositionParts.length > 0) {
        value = styleTextValue || null; // Try to grab from styleText if it exists, otherwise clear since this is a corrupt value
        _logError('CorruptBgPosition', {corrupt: corruptPositionParts});
      }
    }

    // Check for `perspective: 0px` which is invalid since Chrome 93+.
    // Change `perspective: 0px` to `perspective: none`.
    if (property === 'perspective' && value) {
      if (INVALID_PERSPECTIVE_VALUE_RE.test(value)) {
        value = 'none';
      }
    }

    if (styleTextRemoteExists) {
      // Drop properties that exist in styleLess but not in styleText if they are listed as safe to drop
      if (
        styleTextValue === undefined &&
        value !== undefined &&
        property in SAFE_LESS_PROPS_TO_DROP_IF_NO_PUBLISHED_VALUE
      ) {
        _logError('DroppedUnpubProp', {property, value});
        value = null;
      } else if (/^(box|text)-shadow$/.test(property)) {
        // Replace commented-out box/text shadows with styleText value
        if (
          _.startsWith(value, 'none') &&
          styleTextValue !== undefined &&
          styleTextValue !== 'none'
        ) {
          _logError('UncommentShadowPart', {prop: property});
          value = styleTextValue;
        } else if (styleTextValue !== undefined) {
          const styleTextParts = splitCssValue(styleTextValue, ',');
          const styleLessParts = splitCssValue(value, ',');
          const styleLessVisibleParts = _visibleParts(styleLessParts);
          const styleLessHasCorruptValues = _.some(styleLessParts, (part) => {
            // @ts-expect-error - TS2349: This expression is not callable. — ShadowParser.js needs to be typed.
            const shadow = ShadowParser.parseItem(
              StyleVariables.emptyVariableMap
            )(part, 'data', {}, {omitDefaultUnit: true}).data;
            return (
              part !== 'none' &&
              shadow &&
              shadow.blur &&
              shadow.spread &&
              (shadow.blur.value < 0 ||
                (shadow.blur.value > 0 && shadow.blur.unit === null) ||
                (shadow.spread.value !== 0 && shadow.spread.unit === null) ||
                shadow.spread.unit === '%' ||
                shadow.blur.unit === '%')
            );
          });

          if (
            styleTextValue !== 'none' &&
            styleTextParts.length &&
            (styleTextParts.length > styleLessVisibleParts.length ||
              styleLessHasCorruptValues)
          ) {
            // Our parser gets a chance to apply defaults or fix any other issues.
            const parsedValue = parseShadowParts({
              local: {value: styleTextValue},
            });
            value = formatShadowParts(parsedValue.local.value);
            _logError('OverrideShadow', {prop: 'property'});
          }
        }
      } else if (
        /^background-(position|size|repeat|attachment)$/.test(property) &&
        value &&
        styleTextValue !== undefined &&
        !_hasComment(value) &&
        value !== styleTextValue &&
        styleTextValue.split(',').length === value.split(',').length
      ) {
        _logError('MismatchedBgSubprop', {prop: property});
        value = styleTextValue;
      } else if (
        property in SAFE_LESS_PROPS_TO_OVERRIDE &&
        styleTextValue &&
        styleTextValue !== value
      ) {
        // For some properties considered safe, override styleLess versions with styleText to make sure we match published CSS
        if (propDetails.allowSwatches && hasSwatchPlaceholder(value)) {
          // If the styleLess value contains a @swatch_12345678 placeholder, don't clobber styleLess with styleText
        } else {
          if (property !== 'opacity') {
            _logError('OverrideLessWithText', {
              diff: property + '=>' + styleTextValue,
            });
          }

          value = styleTextValue || null;
        }
      } else if (
        property in SAFE_LESS_PROPS_TO_OVERRIDE &&
        !hasValue(styleTextValue) &&
        value
      ) {
        // If a safe prop exists in styleText, but not in styleLess, just remove the styleLess version completely
        _logError('DropLessProp', {prop: property + '=' + value});
        value = null;
      }
    }

    // In some situations, cleanup may lead to properties being dropped
    if (!value || value === null || value.length === 0) {
      propsToDrop.push(property);
    }

    if (value !== oldValue) {
      _upsertLessProp(property, value);
    }
  });

  _removeLessProps(propsToDrop);

  // After all styleText/styleLess conversion, make a last pass on the clean background-image value to determine if we have
  // extra subvalues that have more parts than the source-of-truth background-image property, and drop them
  _normalizeBackgroundImageSubvalues(
    styleTextMap,
    _findLessProp,
    _upsertMultipleLessProps,
    _logError
  );

  const styleLess = lessProps
    // @ts-expect-error - TS7031 - Binding element 'property' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type.
    .map(({property, value}) => property + ': ' + value + ';')
    .join(' ');
  return styleLess;
}

function _hasComment(part: string | null): boolean {
  // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
  return /\/\*/.test(part);
}

function _visibleParts(parts: Array<string>): Array<string> {
  return parts.filter((part) => !_hasComment(part));
}

function _collectBackgroundSubvalues(_findLessProp: (arg1: string) => any) {
  return _.fromPairs(
    _.compact(
      BGIMAGE_ALL_PROPS.map((prop) => {
        const styleLessProp = _findLessProp(prop);
        if (!styleLessProp) {
          return null;
        }

        const parsedSubParts = splitCssValue(styleLessProp.value, ',').map(
          (subpart) => {
            const isHidden = _hasComment(subpart);
            const isReset = /[ a-z0-9%]{2,} \/\* /.test(subpart);
            const subpartChunks = _.compact(
              // @ts-expect-error - TS7006 - Parameter 'chunk' implicitly has an 'any' type.
              subpart.split(/\*/).map((chunk) => {
                return _.trim(chunk, ' ,/');
              })
            );

            return {
              reset: isReset ? subpartChunks[0] : undefined,
              css: subpartChunks[isReset ? 1 : 0],
              hidden: isHidden,
            };
          }
        );

        return styleLessProp ? [styleLessProp.property, parsedSubParts] : null;
      })
    )
  );
}

function _fixCorruptBackgroundParts(
  styleTextMap: any,
  _findLessProp: (arg1: string) => any,
  _upsertLessProp: (arg1: string, arg2: string | null) => boolean,
  _convertBgImagePartToLess: (arg1: string) => string,
  _logError: (arg1: string, arg2?: any | null | undefined) => void
) {
  const styleTextBgImage = styleTextMap['background-image'];
  const bgImageLessProp = _findLessProp('background-image');
  if (!bgImageLessProp || !bgImageLessProp.value || !styleTextBgImage) {
    return;
  }

  const oldLessValue = bgImageLessProp.value;
  const styleTextParts = splitCssValue(styleTextBgImage, ',');
  const styleLessParts = splitCssValue(oldLessValue, ',').filter((part) => {
    return _.trim(part, ' ,') !== '';
  });
  const cleanStyleTextParts = _.compact(
    styleTextParts.filter((part) => {
      return !part || part === 'none' ? null : part;
    })
  );

  // Detect if we have a hidden "none" value in background-image, along with an implied entire first hidden part yet with existing styleText values
  // In this case, we need to unhide the first styleLess part
  if (
    styleLessParts.length &&
    styleTextParts.length === 1 &&
    _.startsWith(styleLessParts[0], 'none /* ') &&
    _visibleParts(styleLessParts).length === 0
  ) {
    const styleLessSplitSubvalues = _collectBackgroundSubvalues(_findLessProp);
    const allFirstPartsReset = _.every(
      Object.keys(styleLessSplitSubvalues),
      (prop) => {
        const splitSubvalues = styleLessSplitSubvalues[prop];
        return (
          splitSubvalues.length > 0 &&
          splitSubvalues[0].reset !== undefined &&
          splitSubvalues[0].hidden
        );
      }
    );

    // Unhide the entire first part across all subprops in styleLess
    if (styleTextBgImage !== 'none' && allFirstPartsReset) {
      const newSubvalueProps = Object.keys(styleLessSplitSubvalues).map(
        (prop) => {
          const transformedParts = styleLessSplitSubvalues[prop].map(
            // @ts-expect-error - TS7006 - Parameter 'chunk' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
            (chunk, index) => {
              const styleTextValue = styleTextMap[prop];
              const {hidden, reset} = chunk;
              const css =
                index === 0 &&
                reset !== undefined &&
                styleTextValue !== undefined &&
                /^background-(position|size|repeat|attachment)$/.test(prop)
                  ? styleTextValue
                  : chunk.css;
              return {css, hidden: hidden && index > 0};
            }
          );

          return {property: prop, value: CssUtils.joinParts(transformedParts)};
        }
      );

      // Override existing styleLess
      if (newSubvalueProps.length) {
        newSubvalueProps.forEach((subvalueProp) => {
          _upsertLessProp(subvalueProp.property, subvalueProp.value);
        });
        _logError('UncommentFirstBgPart', {subprops: newSubvalueProps});
      }
    }
  } else if (_.endsWith(_.trim(oldLessValue), ',')) {
    // Should regenerate background-image values that have corrupt styleLess dangling commas
    const newPropLessParts = cleanStyleTextParts.map(_convertBgImagePartToLess);
    const newLessValue = newPropLessParts.join(', ');

    if (newLessValue !== oldLessValue) {
      _upsertLessProp('background-image', newLessValue);
      BGIMAGE_SUBVALUE_PROPS.forEach((subprop) => {
        const styleTextSubvalue = styleTextMap[subprop];
        if (hasValue(styleTextSubvalue)) {
          const subvalueParts = splitCssValue(styleTextSubvalue, ',');
          const wantedParts = _.take(subvalueParts, cleanStyleTextParts.length);
          const newSubvalue = wantedParts.join(', ');
          _upsertLessProp(subprop, newSubvalue);
        }
      });

      _logError('OverrideLessImages');
    }
  }
}

function _normalizeBackgroundImageSubvalues(
  styleTextMap: any,
  _findLessProp: (arg1: string) => any,
  _upsertMultipleLessProps: (arg1: Map<string, unknown>) => boolean,
  _logError: (arg1: string, arg2?: any | null | undefined) => void
) {
  const styleTextBgImage = styleTextMap['background-image'];
  const backgroundImageProp = _findLessProp('background-image');

  if (
    backgroundImageProp &&
    backgroundImageProp.value &&
    !_hasComment(backgroundImageProp.value)
  ) {
    const cleanedValueParts = splitCssValue(backgroundImageProp.value, ',');
    const styleTextParts =
      styleTextBgImage === undefined
        ? undefined
        : splitCssValue(styleTextBgImage, ',');

    // Only continue if there's a direct match in number of visible parts between styleText and styleLess (if styleText is defined)
    if (
      cleanedValueParts.length &&
      (styleTextParts === undefined ||
        styleTextParts.length === cleanedValueParts.length)
    ) {
      const splitSubvalues = _collectBackgroundSubvalues(_findLessProp);
      const isVirtuallyHidden = splitSubvalues['background-image'].map(
        // @ts-expect-error - TS7006 - Parameter 'part' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
        (part, index) => index > 0 && part && part.css === 'none'
      );
      const visibleSubvalues = _.mapValues(splitSubvalues, (parts) => {
        // @ts-expect-error - TS7006 - Parameter 'part' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
        return parts.filter((part, index) => {
          return part && !part.hidden && !isVirtuallyHidden[index];
        });
      });

      const existingImageProps = BGIMAGE_ALL_PROPS.filter((subprop) =>
        _.has(visibleSubvalues, subprop)
      );
      const cleanedSubvalues = _.compact(
        // @ts-expect-error - TS7006 - Parameter 'masterPart' implicitly has an 'any' type. | TS7006 - Parameter 'masterIndex' implicitly has an 'any' type.
        visibleSubvalues['background-image'].map((masterPart, masterIndex) => {
          if (masterPart.css === 'none' && masterIndex > 0) {
            return null;
          } // Remove any non-zero-index "none" values, which Webflow doesn't support
          return _.fromPairs(
            existingImageProps.map((subvalueProp) => [
              subvalueProp,
              visibleSubvalues[subvalueProp][masterIndex],
            ])
          );
        })
      );

      // @ts-expect-error - TS2345 - Argument of type 'unknown[]' is not assignable to parameter of type '{ [key: string]: number; }[]'.
      const joinedSubvalues = transpose(cleanedSubvalues);
      const newLessValuesMap = _.mapValues(joinedSubvalues, (subvalueParts) => {
        const firstPart = subvalueParts[0];
        const resetValue =
          // @ts-expect-error - TS2339 - Property 'hidden' does not exist on type 'number'. | TS2339 - Property 'reset' does not exist on type 'number'.
          (firstPart && firstPart.hidden && firstPart.reset) || undefined;
        // @ts-expect-error - TS2345 - Argument of type 'number[]' is not assignable to parameter of type '{ css: string | null; hidden?: boolean | undefined; }[]'.
        return CssUtils.joinParts(subvalueParts.filter(hasValue), {
          emptyValue: resetValue,
        });
      });

      // @ts-expect-error - TS2345 - Argument of type '{ [x: string]: string | null; }' is not assignable to parameter of type 'Map<string, unknown>'.
      if (_upsertMultipleLessProps(newLessValuesMap)) {
        _logError('DropBgImageParts');
      }
    }
  }
}

/**
 * Produce a `MixinMap` based on all available variable modes. This data
 * structure is used to actually apply the appropriate CSS variable overrides
 * for each mode while rendering CSS.
 *
 * IMPORTANT: This should only be used within a non-publishing context, as it
 * does NOT omit deleted variables that are not referenced in any concrete
 * context.
 */
export const getMixinMap = weakMemo((fonts: Font[]) =>
  weakMemo((slugMap: Plugins.PluginsType['slugMap']) =>
    weakMemo((variables: CssVariablesStateType['variables']) =>
      weakMemo(
        (
          variableCollections: CssVariablesStateType['variableCollections']
        ): MixinMap => {
          const result: MixinMap = {};
          const variablesByCollectionId = variables.reduce(
            (
              acc: {
                [
                  collectionId: StyleVariables.VariableCollectionId
                ]: StyleVariables.AnyVariableType[];
              },
              variable
            ) => {
              const collection = StyleVariables.getVariableCollection(
                variable,
                variableCollections
              );
              if (collection == null) return acc;
              let collectionVariables = acc[collection.id];
              if (collectionVariables == null) {
                collectionVariables = [];
                acc[collection.id] = collectionVariables;
              }
              collectionVariables.push(variable);
              return acc;
            },
            {}
          );

          for (const [collectionId, collection] of variableCollections) {
            const mixinKey: MixinKey = `---mode--${collectionId}`;
            const collectionMixins: MixinMap[MixinKey] = {};
            for (const modeId of Object.keys({
              ...collection.modes,
              base: {
                id: StyleVariables.BASE_VARIABLE_MODE_ID,
                name: 'Base',
              } as StyleVariables.VariableMode,
            })) {
              const collectionVariables = variablesByCollectionId[collectionId];
              if (collectionVariables == null) continue;
              collectionMixins[modeId] = defer(() => {
                const properties: string[] = [];
                for (const variable of collectionVariables) {
                  if (!variable) continue;
                  properties.push(
                    `--${StyleVariables.renderCssVariableName(
                      variables,
                      variableCollections,
                      slugMap,
                      variable.id
                    )}: ${StyleVariables.renderAnyCssValue((variable.modes ? variable.modes[modeId] : null) ?? variable.value, variables, variableCollections, slugMap, fonts)};`
                  );
                }
                return properties.join('');
              });
            }

            const collectionVariables = variablesByCollectionId[collectionId];
            if (collectionVariables != null) {
              collectionMixins[StyleVariables.INHERIT_VARIABLE_MODE_ID] = defer(
                () => {
                  const properties: string[] = [];
                  for (const variable of collectionVariables) {
                    if (!variable) continue;
                    properties.push(
                      `--${StyleVariables.renderCssVariableName(
                        variables,
                        variableCollections,
                        slugMap,
                        variable.id
                      )}: inherit;`
                    );
                  }
                  return properties.join('');
                }
              );
            }

            result[mixinKey] = collectionMixins;
          }

          return result;
        }
      )
    )
  )
);

export const compileLess = (
  styleLess: string,
  {
    slugMap,
    beautify,
    styleSheetImageResolver,
    cssVariablesState,
    fonts,
    mut_seenMixins,
  }: {
    slugMap: Plugins.PluginsType['slugMap'];
    beautify?: boolean;
    styleSheetImageResolver?: StyleSheetImageResolverInterface;
    cssVariablesState?: CssVariablesStateType;
    fonts?: Font[];
    mut_seenMixins?: Set<AppliedMixinKey>;
  }
) => {
  const getImageUrl = (id: string): string | null => {
    if (!styleSheetImageResolver) {
      return null;
    }
    const imageInfo =
      styleSheetImageResolver.getById(id) ||
      styleSheetImageResolver.getByGuid(id);
    return imageInfo?.cdnUrl ?? null;
  };

  return sharedCompileLess(styleLess, {
    slugMap,
    beautify,
    getImageUrl: styleSheetImageResolver ? getImageUrl : undefined,
    swatches: cssVariablesState ? cssVariablesState.swatches : undefined,
    variables: cssVariablesState ? cssVariablesState.variables : undefined,
    variableCollections: cssVariablesState
      ? cssVariablesState.variableCollections
      : StyleVariables.emptyVariableCollectionMap,
    mixins: cssVariablesState
      ? defer(() =>
          getMixinMap(fonts ?? emptyArray)(slugMap)(
            cssVariablesState.variables
          )(cssVariablesState.variableCollections)
        )
      : undefined,
    mut_seenMixins,
    fonts,
  });
};

export function compileLessWithSeenMixins(
  styleLess: string,
  {
    slugMap,
    beautify,
    styleSheetImageResolver,
    cssVariablesState,
    fonts,
  }: {
    slugMap: Plugins.PluginsType['slugMap'];
    beautify?: boolean;
    styleSheetImageResolver?: StyleSheetImageResolverInterface;
    cssVariablesState?: CssVariablesStateType;
    fonts?: Font[];
  }
): {
  styleText: string;
  seenMixins: Set<AppliedMixinKey>;
} {
  const seenMixins = new Set<AppliedMixinKey>();
  const styleText = compileLess(styleLess, {
    slugMap,
    beautify,
    styleSheetImageResolver,
    cssVariablesState,
    fonts,
    mut_seenMixins: seenMixins,
  });
  return {
    styleText,
    seenMixins,
  };
}

export const forTestSuite = {
  getMixinMap,
};
